<template>
    <SmithsChartCard :has-data="hasData" :loading="loading" title="System Events">
        <template #sidebar>
            <SidebarKpi label="System Errors" :value="data?.kpis?.totalSystemErrors ?? 0" />
            <SidebarKpi label="Startups" :value="data?.kpis?.totalStartups ?? 0" />
            <SidebarKpi label="Fault Resets" :value="data?.kpis?.totalFaultResets ?? 0" />
        </template>
        <c-chart :key="componentKey" :custom-tooltips="false" :data="chartDataset" :options="chartOptions" class="h-full" type="line" />
    </SmithsChartCard>
</template>

<script setup>
import { computed, ref } from 'vue';
import { CChart } from '@coreui/vue-chartjs';
import SmithsChartCard from './SmithsChartCard.vue';
import SidebarKpi from './SidebarKpi.vue';
import { getBaseChartOptions, chartColors, lightTooltip } from '@/Utils/chartDefaults';

const props = defineProps({
    data: { type: Object, default: null },
    loading: { type: Boolean, default: false },
});

const componentKey = ref(0);
const hasData = computed(() => props.data?.labels?.length > 0);

const chartDataset = computed(() => {
    if (!hasData.value) return { labels: [], datasets: [] };

    return {
        labels: props.data.labels,
        datasets: [
            {
                label: 'System Errors',
                data: props.data.systemErrors,
                borderColor: chartColors.red,
                backgroundColor: chartColors.redFillLight,
                borderWidth: 2,
                tension: 0.3,
                pointRadius: 2,
                pointHoverRadius: 4,
            },
            {
                label: 'Fault Resets',
                data: props.data.faultResets,
                borderColor: chartColors.amber,
                backgroundColor: chartColors.amberFill,
                borderWidth: 2,
                tension: 0.3,
                pointRadius: 2,
                pointHoverRadius: 4,
            },
            {
                label: 'Startups',
                data: props.data.startups,
                borderColor: chartColors.primary,
                backgroundColor: chartColors.primaryFill,
                borderWidth: 2,
                tension: 0.3,
                pointRadius: 2,
                pointHoverRadius: 4,
            },
        ],
    };
});

const chartOptions = computed(() => ({
    ...getBaseChartOptions(),
    interaction: { mode: 'index', intersect: false },
    scales: {
        x: {
            grid: { display: false },
            ticks: { font: { size: 10 }, maxRotation: 45 },
        },
        y: {
            beginAtZero: true,
            grid: { drawBorder: false },
            ticks: { stepSize: 1 },
        },
    },
    plugins: {
        ...getBaseChartOptions().plugins,
        legend: {
            display: true,
            position: 'bottom',
            labels: {
                usePointStyle: true,
                pointStyle: 'circle',
                boxWidth: 8,
                generateLabels: (chart) =>
                    chart.data.datasets.map((ds, i) => ({
                        text: ds.label,
                        fillStyle: ds.borderColor,
                        strokeStyle: ds.borderColor,
                        hidden: !chart.isDatasetVisible(i),
                        datasetIndex: i,
                    })),
            },
        },
        tooltip: { ...lightTooltip, enabled: true, mode: 'index', intersect: false },
    },
}));
</script>
